import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBluesky, faInstagram, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import { AchievementCelebration, openBlueskyShare, openTwitterShare } from "../../utils/achievementCelebration";
import { generateCelebrationStoryImage, isWebShareFilesSupported } from "../../utils/instagramShare";
import * as analytics from "../../utils/analytics";
import styles from "./achievementCelebrationDialog.module.scss";

type AchievementCelebrationDialogProps = {
    achievement: AchievementCelebration;
    continueTo: string;
    onDismiss: () => void;
};

// The site's own domain, baked into the generated share image (not just a
// link) so the branding survives the image being downloaded/shared
// standalone - see achievementCelebration.ts's SITE_URL for the same string
// used elsewhere.
const BRAND_URL = "nihongotech.teqseq.com";

type InstagramShareState = "idle" | "generating" | "ready" | "error";

export default function AchievementCelebrationDialog({ achievement, continueTo, onDismiss }: AchievementCelebrationDialogProps) {
    const { t } = useTranslation("common");
    const dialogRef = useRef<HTMLDivElement>(null);
    const continueRef = useRef<HTMLAnchorElement>(null);
    const [shareErrorPlatform, setShareErrorPlatform] = useState<"bluesky" | "x" | null>(null);
    const [instagramState, setInstagramState] = useState<InstagramShareState>("idle");
    const [instagramFile, setInstagramFile] = useState<File | null>(null);
    const [instagramPreviewUrl, setInstagramPreviewUrl] = useState<string | null>(null);
    const featureTitle = achievement.kind === "feature"
        ? t(`achievementDialog.features.${achievement.discoveryId}.title`, { defaultValue: achievement.title })
        : achievement.title;
    const headingText = achievement.kind === "level" ? t("achievementDialog.levelTitle", { level: achievement.level }) : featureTitle;
    const continueLabel = achievement.kind === "level"
        ? t("achievementDialog.actions.level")
        : achievement.kind === "achievement"
            ? t("achievementDialog.actions.achievement")
            : achievement.kind === "feature"
                ? t("achievementDialog.actions.feature", { title: featureTitle })
                : t("achievementDialog.continueLearning");
    const canShareInstagramNatively = instagramFile !== null && isWebShareFilesSupported(instagramFile);

    // Revokes the previous preview's object URL whenever it is replaced
    // (regenerated) or the dialog unmounts, so the browser doesn't leak it.
    useEffect(() => () => {
        if (instagramPreviewUrl) URL.revokeObjectURL(instagramPreviewUrl);
    }, [instagramPreviewUrl]);

    const dismiss = useCallback(() => {
        analytics.track("achievement_celebration_dismissed", { achievement_type: achievement.kind });
        onDismiss();
    }, [achievement.kind, onDismiss]);

    useEffect(() => {
        continueRef.current?.focus();
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") dismiss();
            if (event.key !== "Tab" || !dialogRef.current) return;
            const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>("a[href], button:not([disabled])"));
            if (!focusable.length) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener("keydown", onKeyDown);
        return () => document.removeEventListener("keydown", onKeyDown);
    }, [dismiss]);

    function shareOnBluesky() {
        analytics.track("achievement_bluesky_share_selected", { achievement_type: achievement.kind });
        setShareErrorPlatform(openBlueskyShare(achievement) ? null : "bluesky");
    }

    function shareOnX() {
        analytics.track("achievement_x_share_selected", { achievement_type: achievement.kind });
        setShareErrorPlatform(openTwitterShare(achievement) ? null : "x");
    }

    async function shareOnInstagram() {
        analytics.track("achievement_instagram_share_selected", { achievement_type: achievement.kind });
        setInstagramState("generating");
        try {
            const blob = await generateCelebrationStoryImage({
                eyebrow: t("achievementDialog.unlocked").toUpperCase(),
                title: headingText,
                xpLabel: achievement.xp !== undefined ? `+${achievement.xp} XP` : undefined,
                invitation: t("achievementDialog.instagramStory.invitation"),
                brandName: "NihongoTech",
                brandUrl: BRAND_URL,
            });
            setInstagramFile(new File([blob], "nihongotech-story.png", { type: "image/png" }));
            setInstagramPreviewUrl(URL.createObjectURL(blob));
            setInstagramState("ready");
        } catch {
            setInstagramState("error");
        }
    }

    async function shareInstagramNatively() {
        if (!instagramFile) return;
        analytics.track("achievement_instagram_native_share_selected", { achievement_type: achievement.kind });
        try {
            await navigator.share({ files: [instagramFile] });
        } catch (error) {
            if ((error as DOMException)?.name !== "AbortError") setInstagramState("error");
        }
    }

    return (
        <div className={styles.backdrop} role="presentation">
            <div className={styles.dialog} role="dialog" aria-modal="true" aria-labelledby="achievement-title" aria-describedby="achievement-message" ref={dialogRef}>
                <p className={styles.eyebrow}>{t("achievementDialog.unlocked")}</p>
                <h2 id="achievement-title">{headingText}</h2>
                <p id="achievement-message">{achievement.kind === "feature" ? t(`achievementDialog.features.${achievement.discoveryId}.message`, { defaultValue: achievement.message }) : t(`achievementDialog.messages.${achievement.kind}`, { level: achievement.level, defaultValue: achievement.message })}</p>
                {achievement.xp !== undefined && <p className={styles.xp}>+{achievement.xp} XP</p>}
                <div className={styles.actions}>
                    <Link ref={continueRef} to={continueTo} className={styles.continue} onClick={() => {
                        analytics.track("achievement_continue_learning_selected", { achievement_type: achievement.kind });
                        onDismiss();
                    }}>{continueLabel}</Link>
                    {achievement.shareable && (
                        <div className={styles.shareRow}>
                            <button type="button" className={styles.share} onClick={shareOnBluesky}>
                                <FontAwesomeIcon icon={faBluesky} aria-hidden="true" className={styles.shareIcon} />
                                {t("achievementDialog.shareOnBluesky")}
                            </button>
                            <button type="button" className={styles.share} onClick={shareOnX}>
                                <FontAwesomeIcon icon={faXTwitter} aria-hidden="true" className={styles.shareIcon} />
                                {t("achievementDialog.shareOnX")}
                            </button>
                            <button type="button" className={styles.share} onClick={shareOnInstagram} disabled={instagramState === "generating"} aria-busy={instagramState === "generating"}>
                                <FontAwesomeIcon icon={faInstagram} aria-hidden="true" className={styles.shareIcon} />
                                {t("achievementDialog.shareOnInstagram")}
                            </button>
                        </div>
                    )}
                    <button type="button" className={styles.dismiss} onClick={dismiss}>{t("achievementDialog.notNow")}</button>
                </div>
                {shareErrorPlatform === "bluesky" && <p className={styles.error} role="status">{t("achievementDialog.shareError")}</p>}
                {shareErrorPlatform === "x" && <p className={styles.error} role="status">{t("achievementDialog.shareErrorX")}</p>}
                {instagramState === "generating" && (
                    <p className={styles.instagramStatus} role="status">{t("achievementDialog.instagramStory.generating")}</p>
                )}
                {instagramState === "ready" && instagramPreviewUrl && (
                    <div className={styles.instagramPreview}>
                        <img src={instagramPreviewUrl} alt={t("achievementDialog.instagramStory.previewAlt")} className={styles.instagramPreviewImage} />
                        <div className={styles.instagramPreviewActions}>
                            {canShareInstagramNatively && (
                                <button type="button" className={styles.instagramPreviewButton} onClick={shareInstagramNatively}>
                                    {t("achievementDialog.instagramStory.shareButton")}
                                </button>
                            )}
                            <a href={instagramPreviewUrl} download="nihongotech-story.png" className={styles.instagramDownload}>
                                {t("achievementDialog.instagramStory.downloadButton")}
                            </a>
                        </div>
                        <p className={styles.instagramInstructions}>{t("achievementDialog.instagramStory.instructions")}</p>
                    </div>
                )}
                {instagramState === "error" && (
                    <p className={styles.error} role="status">{t("achievementDialog.instagramStory.error")}</p>
                )}
            </div>
        </div>
    );
}
